import { formatMoney } from '../../cart/cart-money'
import type { VolumePricingCurve } from '../types'

const CHART_WIDTH = 280
const CHART_HEIGHT = 110
const PAD_X = 8
const PAD_Y = 12

/**
 * Decorative price/quantity curve (UID-159, ADR-0058) — `aria-hidden`
 * because it carries NO information that isn't also in
 * {@link VolumeBreaksList} (the same tier data, rendered as a real list —
 * WCAG 1.1.1/1.4.1's "non-text content has a text alternative"). Navy
 * brand tokens only (`--color-brand-primary`), never the retired Fictiv
 * green, per the design system.
 */
export function VolumeCurveChart({
  curve,
  activeMinQuantity,
}: {
  readonly curve: VolumePricingCurve
  readonly activeMinQuantity: number | null
}) {
  const tiers = curve.tiers
  if (tiers.length === 0) return null

  const prices = tiers.map((tier) => Number(tier.unitPriceAmount))
  const maxPrice = Math.max(...prices)
  const minPrice = Math.min(...prices)
  const plotBottom = CHART_HEIGHT - PAD_Y

  function xFor(qty: number): number {
    return PAD_X + (Math.min(qty, curve.cap) / curve.cap) * (CHART_WIDTH - PAD_X * 2)
  }
  function yFor(price: number): number {
    if (maxPrice === minPrice) return CHART_HEIGHT / 2
    const t = (price - minPrice) / (maxPrice - minPrice)
    return PAD_Y + (1 - t) * (plotBottom - PAD_Y)
  }

  const points = tiers.map((tier) => ({
    tier,
    x: xFor(tier.minQuantity),
    y: yFor(Number(tier.unitPriceAmount)),
  }))
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
  const lastPoint = points[points.length - 1]
  const areaPath = lastPoint
    ? `${linePath} L${lastPoint.x},${plotBottom} L${points[0]?.x ?? 0},${plotBottom} Z`
    : ''
  // Flat grey dashed continuation past the last real tier, up to the right
  // edge — the visual echo of the "RFQ beyond" legend/caption; purely
  // decorative, the caption text carries the actual meaning.
  const dashPath = lastPoint
    ? `M${lastPoint.x},${lastPoint.y} L${CHART_WIDTH - PAD_X},${lastPoint.y}`
    : ''

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="volume-curve-chart"
      aria-hidden="true"
      focusable="false"
    >
      <path d={areaPath} className="volume-curve-area" />
      <path d={dashPath} className="volume-curve-dash" />
      <path d={linePath} className="volume-curve-line" />
      {points.map((p) => (
        <circle
          key={p.tier.minQuantity}
          cx={p.x}
          cy={p.y}
          r={activeMinQuantity === p.tier.minQuantity ? 5 : 3.5}
          className={
            activeMinQuantity === p.tier.minQuantity
              ? 'volume-curve-marker volume-curve-marker-active'
              : 'volume-curve-marker'
          }
        />
      ))}
    </svg>
  )
}

/**
 * The REAL accessible data (WCAG 1.1.1/1.4.1) — every rung the chart above
 * plots, as a real focusable list. This is also the keyboard equivalent of
 * a mouse "hover the graph to explore quantity" crosshair (AC4): focusing
 * (Tab) or hovering a row sets the SAME `activeMinQuantity` the chart reads
 * to highlight its marker — one piece of state drives both input modes, so
 * there is no separate keyboard-only code path to fall out of sync.
 *
 * `variant="rows"` (the popover) is a compact one-line-per-tier list;
 * `variant="cards"` (the Part-detail panel) matches the design's "Qty {q} /
 * {u} per unit" break-card layout (`dc.html:1620-1621`).
 */
export function VolumeBreaksList({
  curve,
  currency,
  activeMinQuantity,
  onActiveChange,
  variant = 'rows',
}: {
  readonly curve: VolumePricingCurve
  readonly currency: string
  readonly activeMinQuantity: number | null
  readonly onActiveChange: (minQuantity: number) => void
  readonly variant?: 'rows' | 'cards'
}) {
  return (
    <ul
      className={variant === 'cards' ? 'volume-curve-breaks-cards' : 'volume-curve-breaks-rows'}
      aria-label="Unit price by quantity"
    >
      {curve.tiers.map((tier) => (
        <li key={tier.minQuantity}>
          <button
            type="button"
            className="volume-curve-break-item"
            aria-pressed={activeMinQuantity === tier.minQuantity}
            onFocus={() => onActiveChange(tier.minQuantity)}
            onMouseEnter={() => onActiveChange(tier.minQuantity)}
            onClick={() => onActiveChange(tier.minQuantity)}
          >
            <span className="volume-curve-break-qty">Qty {tier.minQuantity}</span>
            <span className="volume-curve-break-unit">
              {formatMoney(tier.unitPriceAmount, currency)}
              {variant === 'cards' && (
                <span className="caption-text volume-curve-break-suffix"> per unit</span>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/** Legend for the Part-detail panel (AC2, `dc.html:1606`): "Algorithmic to
 * {cap} pcs" (solid navy) vs "RFQ beyond" (dashed grey) — matches the
 * chart's own line/dash treatment above. */
export function VolumeCurveLegend({ cap }: { readonly cap: number }) {
  return (
    <div className="volume-curve-legend">
      <span className="volume-curve-legend-item">
        <span
          className="volume-curve-legend-swatch volume-curve-legend-swatch-solid"
          aria-hidden="true"
        />
        Algorithmic to {cap} pcs
      </span>
      <span className="volume-curve-legend-item">
        <span
          className="volume-curve-legend-swatch volume-curve-legend-swatch-dashed"
          aria-hidden="true"
        />
        RFQ beyond
      </span>
    </div>
  )
}
